import { Injectable, inject, signal, ChangeDetectorRef, NgZone } from '@angular/core';
import { QuestionView } from '../models';
import { FormStore } from '../store/form.store';

/**
 * Service to handle drag and drop operations for form questions
 */
@Injectable({
  providedIn: 'root'
})
export class FormDragDropService {
  private formStore = inject(FormStore);

  // Drag state signals
  readonly draggedQuestionId = signal<string | null>(null);
  readonly dragOverIndex = signal<number | null>(null);

  /**
   * Where the question will land, counted in gaps rather than rows: 0 is above
   * the first question, 1 between the first and second, and `questions.length`
   * below the last. Highlighting a row can't say whether the question goes
   * above or below it; a gap says exactly where it lands.
   */
  readonly dropGapIndex = signal<number | null>(null);

  /**
   * Handle drag start event
   */
  onDragStart(event: DragEvent, questionId: string): void {
    console.log('Drag started for question:', questionId);
    this.draggedQuestionId.set(questionId);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', questionId);
    }
  }

  /**
   * Handle drag over event
   */
  onDragOver(event: DragEvent, index: number): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.dragOverIndex.set(index);
    this.dropGapIndex.set(this.gapUnderPointer(event, index));
  }

  /**
   * The gap the pointer is closest to: above this row in its top half, below
   * it in its bottom half.
   */
  private gapUnderPointer(event: DragEvent, index: number): number {
    const row = event.currentTarget as HTMLElement | null;
    const rect = row?.getBoundingClientRect?.();
    if (!rect || !rect.height) {
      return index;
    }
    return event.clientY > rect.top + rect.height / 2 ? index + 1 : index;
  }

  /**
   * Handle drag leave event
   */
  onDragLeave(): void {
    this.dragOverIndex.set(null);
    this.dropGapIndex.set(null);
  }

  /**
   * Handle drop event
   * @param event The drag event
   * @param dropIndex The index where the item is being dropped
   * @param currentQuestions Current questions array
   * @param recalculateQuestionNumbers Function to recalculate question numbers
   * @param onQuestionsUpdated Callback when questions are updated
   */
  onDrop(
    event: DragEvent,
    dropIndex: number,
    currentQuestions: QuestionView[],
    recalculateQuestionNumbers: (questions: QuestionView[]) => QuestionView[],
    onQuestionsUpdated: (questions: QuestionView[]) => void,
    ngZone: NgZone,
    cdr: ChangeDetectorRef
  ): void {
    event.preventDefault();
    event.stopPropagation();
    const gapIndex = this.dropGapIndex();
    this.dragOverIndex.set(null);
    this.dropGapIndex.set(null);

    console.log('Drop event triggered at index:', dropIndex, 'draggedQuestionId:', this.draggedQuestionId());

    // Get the dragged question ID from dataTransfer or component state
    let draggedId: string | null = this.draggedQuestionId();

    if (draggedId === null && event.dataTransfer) {
      const data = event.dataTransfer.getData('text/plain');
      if (data) {
        draggedId = data;
        console.log('Retrieved dragged ID from dataTransfer:', draggedId);
      }
    }

    if (draggedId === null) {
      console.warn('Drop event: No dragged question ID found');
      this.draggedQuestionId.set(null);
      return;
    }

    const dragIndex = currentQuestions.findIndex(q => q.id === draggedId);
    if (dragIndex === -1) {
      this.draggedQuestionId.set(null);
      return;
    }

    // The gap the pointer was last over; falling back to the row's own index
    // covers a drop that arrives without a preceding dragover.
    const gap = Math.max(0, Math.min(gapIndex ?? dropIndex, currentQuestions.length));

    // Taking the question out shifts everything after it up one, so a gap
    // below the question's old position loses one place.
    const insertIndex = Math.max(0, Math.min(gap > dragIndex ? gap - 1 : gap, currentQuestions.length - 1));

    // Already there: the gaps either side of a question are both its own place.
    if (insertIndex === dragIndex) {
      this.draggedQuestionId.set(null);
      return;
    }

    const draggedQuestion = { ...currentQuestions[dragIndex] };
    const reorderedQuestions = [...currentQuestions];
    reorderedQuestions.splice(dragIndex, 1);

    // Insert at the new position
    reorderedQuestions.splice(insertIndex, 0, draggedQuestion);

    // Update order to match the new array order
    const questionsWithUpdatedOrder = reorderedQuestions.map((q, index) => ({
      ...q,
      order: index
    }));

    // Recalculate question numbers
    const updatedQuestions = recalculateQuestionNumbers(questionsWithUpdatedOrder);

    console.log('Reordering questions:', {
      dragIndex,
      dropIndex,
      insertIndex,
      before: currentQuestions.map((q: QuestionView) => ({ id: q.id, number: q.questionNumber, order: q.order })),
      after: updatedQuestions.map((q: QuestionView) => ({ id: q.id, number: q.questionNumber, order: q.order }))
    });

    // Update via callback for instant UI update
    onQuestionsUpdated([...updatedQuestions]);

    // Trigger immediate change detection
    ngZone.run(() => {
      cdr.markForCheck();
      cdr.detectChanges();
    });

    // Update state — patchState is synchronous, so the store signal is current immediately.
    this.formStore.reorderQuestions(updatedQuestions);

    const storeQuestions = this.formStore.questions();
    onQuestionsUpdated([...storeQuestions]);

    ngZone.run(() => {
      cdr.markForCheck();
      cdr.detectChanges();
    });

    this.draggedQuestionId.set(null);
  }
}

